import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlobalFilterState } from "@/global_filter";
import { Activity, activityState, fetchActivities, startPolling, stopPolling } from '@/stores/activity';
import { classes } from '@/utils';
import { parseISO } from "date-fns";
import { Bell } from 'lucide-react';
import { useEffect, useMemo } from 'preact/hooks';
import { ActivityList } from './activity-list';
import { MessageList } from './message-list';
import { NoteList } from './note-list';
import { QueueList } from './queue-list';
import { RecapList } from './recap-list';

const VALID_TABS = ['queue', 'internal', 'external', 'activity', 'recap'] as const;
type ValidTab = typeof VALID_TABS[number];

function getTabFromUrl(): ValidTab {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  if (tab && VALID_TABS.includes(tab as ValidTab)) {
    return tab as ValidTab;
  }
  return 'recap';
}

function updateTabInUrl(tab: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', tab);
  window.history.replaceState({}, '', url.toString());
}

interface ActivityFeedProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isStandalone?: boolean;
  onClose?: () => void;
}

interface TabConfig {
  value: string;
  label: string;
  component: (props: {
    isStandalone: boolean;
    onClose?: () => void;
    activities?: Activity[];
    isLoading?: boolean;
  }) => any;
}

const TABS: TabConfig[] = [
  {
    value: 'internal',
    label: 'Internal',
    component: NoteList,
  },
  {
    value: 'external',
    label: 'External',
    component: MessageList,
  },
  {
    value: 'activity',
    label: 'Activity',
    component: ({ isStandalone, onClose, activities, isLoading }) => (
      <ActivityList
        activities={activities || []}
        isLoading={isLoading || false}
        isStandalone={isStandalone}
        onClose={onClose}
      />
    ),
  },
  {
    value: 'queue',
    label: 'Queue',
    component: QueueList,
  },
  {
    value: 'recap',
    label: 'Recap',
    component: RecapList,
  },
];

function ActivityHeader({ lastRefresh }: { lastRefresh: Date | null }) {
  return (
    <div className="flex items-center justify-between p-6 border-b">
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">Notification / Activity Feed</h1>
      </div>
      <div className="flex items-center space-x-4">
        {/* {lastRefresh && (
          <span className="text-sm text-muted-foreground">
            Updated {formatDistanceToNow(lastRefresh, { addSuffix: true })}
          </span>
        )} */}
      </div>
    </div>
  );
}

export function ActivityFeed({ open, onOpenChange, isStandalone = false, onClose }: ActivityFeedProps) {
  const { activities, isLoading, error, lastRefresh } = activityState.use();
  
  // Get default tab from URL query parameter
  const defaultTab = useMemo(() => getTabFromUrl(), []);

  useEffect(() => {
    if (isStandalone || open) {
      fetchActivities();
      startPolling();
    } else if (!isStandalone && !open) {
      stopPolling();
    }
    return () => stopPolling();
  }, [open, isStandalone]);

  // Strip auto-synced date/distributor query params (not from "Share recap") so they do not block the 60-day default
  useEffect(() => {
    if (!isStandalone) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("shared") === "1") return;
    const keys = ["start_date", "end_date", "source", "date_kind"] as const;
    const next = new URLSearchParams(params);
    let changed = false;
    for (const key of keys) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) {
      const qs = next.toString();
      const path = window.location.pathname;
      window.history.replaceState({}, "", qs ? `${path}?${qs}` : path);
    }
  }, [isStandalone]);

  // When loading a link from "Share recap" (shared=1), apply URL params to global filter
  useEffect(() => {
    if (!isStandalone) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") !== "recap" || params.get("shared") !== "1") return;
    const start_date = params.get("start_date");
    const end_date = params.get("end_date");
    const source = params.get("source");
    const date_kind = params.get("date_kind");
    if (start_date || end_date || source || date_kind) {
      GlobalFilterState.set((s) => {
        const next = { ...s };
        if (start_date) next.startDate = parseISO(start_date);
        if (end_date) next.endDate = parseISO(end_date);
        if (source) next.distributor = source;
        if (date_kind === "invoice" || date_kind === "check" || date_kind === "posting" || date_kind === "deposit") {
          next.dateKind = date_kind;
        }
        return next;
      });
    }
  }, [isStandalone]);

  const content = (
    <>
      <ActivityHeader lastRefresh={lastRefresh} />
      
      <Tabs 
        defaultValue={defaultTab} 
        className="w-full"
        onValueChange={(value) => {
          if (isStandalone) {
            updateTabInUrl(value);
          }
        }}
      >
        <TabsList 
          className={classes(
            "grid w-full grid-cols-5 mb-2",
            "border-0 bg-transparent",
            "[&>*]:rounded-none [&>*]:border-b-2 [&>*]:border-transparent",
            "[&>*[data-state=active]]:border-plue-900 [&>*[data-state=active]]:shadow-none"
          )}
        >
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="internal">Internal</TabsTrigger>
          <TabsTrigger value="external">External</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="recap">Recap</TabsTrigger>
        </TabsList>

        {TABS.map(({ value, component: Component }) => (
          <TabsContent key={value} value={value} className="mt-0">
            {value === 'activity' && error ? (
              <div className="p-6 text-destructive">{error}</div>
            ) : (
              <Component
                activities={value === 'activity' ? activities : undefined}
                isLoading={value === 'activity' ? isLoading : false}
                isStandalone={isStandalone}
                onClose={!isStandalone ? onOpenChange?.bind(null, false) : undefined}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </>
  );

  if (isStandalone) {
    return <div className="container py-6">{content}</div>;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:w-[600px] lg:w-[800px] p-0"
      >
        {content}
      </SheetContent>
    </Sheet>
  );
} 